<script>
  // @ts-check
  import { onMount } from "svelte";

  import DynamicHeadingContext from "../../atoms/DynamicHeadingContext.svelte";
  import DynamicHeading from "../../atoms/DynamicHeading.svelte";
  import Image from "../../atoms/Image.svelte";
  import Markdown from "../../atoms/Markdown.svelte";

  import { getTheme, isSmallOrMediumBreakpoint } from "../../utilities";

  /** Primarily used for testing
   * @type {string} */
  export let id = "";

  /**
   * @type { "split" | "stacked" }
   */
  export let layout = "split";

  /**
   * The theme of the form or confirmation view
   * @type {import('../../utilities/themes.js').ThemeName}
   */
  export let theme = "lightGrey";

  /**
   * Optional featured image
   * @type {import('../../types/shared.js').Image} Image
   */
  export let featuredImage = undefined;

  /** The title of the form or confirmation view
   * @type {string}
   * */
  export let title = "";

  /**
   * The description of the form or confirmation view
   * @type {string}
   */
  export let description = "";

  /** @type {boolean} whether the confirmation view has html embed as cta */
  export let hasEmbedCta = false;

  /** @type {boolean} */
  export let isFormSubmitSuccess = false;

  const hasFeatureImage = !!featuredImage?.src && !!featuredImage?.alt;

  $: currentTheme = getTheme(theme);

  $: useSplitLayout =
    layout === "split" || (isFormSubmitSuccess && !isSmallOrMediumBreakpoint(screenWidth));

  $: containerClass = useSplitLayout
    ? !hasFeatureImage
      ? `min-h-full lg:min-h-[37.25rem] xl:min-h-[46.5rem] px-4 py-8 min-[390px]:px-10 md:px-10 md:py-[50px] lg:p-[48px]`
      : `p-2 md:p-[10px] lg:p-3 xl:p-4`
    : "gap-y-6 px-4 py-8 min-[390px]:px-10 md:gap-y-15 md:px-10 md:py-[50px] lg:p-[48px] max-w-[1161px] mx-auto";

  const contentClass =
    layout === "split"
      ? !hasFeatureImage
        ? "p-0 lg:py-6"
        : "gap-y-6 md:gap-15 px-4 py-0 md:px-10 lg:px-0 lg:py-6"
      : "";
  const descriptionClass = "body-md";
  const headingProps = isFormSubmitSuccess
    ? {
        tabindex: "-1",
        "aria-live": "assertive"
      }
    : {};

  export let submitSuccessTextElement = undefined;
  onMount(() => {
    if (isFormSubmitSuccess) {
      submitSuccessTextElement?.focus();
    }
  });

  let screenWidth;
</script>

<svelte:window bind:innerWidth={screenWidth} />

<DynamicHeadingContext>
  <!-- layout: split OR stacked on larger screens -->
  {#if useSplitLayout}
    <div
      {id}
      class="{currentTheme?.backgroundColor} {currentTheme?.textColor} relative flex min-h-full flex-col gap-[30px] md:gap-15 lg:flex-row lg:gap-[72px] {containerClass}"
    >
      <!-- left -->
      {#if hasFeatureImage}
        <div class="flex-1">
          <Image
            id="{id}-featured-image"
            {...featuredImage}
            aspectRatioClasses="aspect-3/2 lg:aspect-3/4"
          />
        </div>
      {:else}
        <div class="flex flex-1 flex-col gap-y-4 md:gap-y-5 {contentClass}">
          <DynamicHeading
            id="{id}-title"
            class="heading-md"
            bind:ref={submitSuccessTextElement}
            {...headingProps}
          >
            {title}
          </DynamicHeading>

          {#if !isFormSubmitSuccess || hasEmbedCta}
            <Markdown input={description} class={descriptionClass} id="{id}-description" />
          {/if}
        </div>
      {/if}

      <!-- right -->
      <div class="flex flex-1 flex-col {contentClass}">
        {#if hasFeatureImage}
          <div class="flex grow flex-col gap-y-6">
            <DynamicHeading
              id="{id}-title"
              class="heading-md"
              bind:ref={submitSuccessTextElement}
              {...headingProps}
            >
              {title}
            </DynamicHeading>

            {#if !isFormSubmitSuccess}
              <Markdown input={description} class={descriptionClass} id="{id}-description" />
            {/if}
          </div>
        {/if}

        <div class={!isFormSubmitSuccess ? "py-8 md:pt-4 lg:pt-2" : "lg:h-1/2"}>
          <slot name="main">Main content goes here</slot>
        </div>
      </div>
    </div>
  {:else}
    <!-- layout: stacked -->
    <div
      id="form-view-container-{id}"
      class="{currentTheme.backgroundColor} {currentTheme?.textColor} flex flex-col {containerClass}"
    >
      <div class="flex flex-col gap-y-5 {contentClass}">
        <DynamicHeading id="{id}-title" class="heading-md">
          {title}
        </DynamicHeading>
        {#if !isFormSubmitSuccess || hasEmbedCta}
          <Markdown input={description} class={descriptionClass} id="{id}-description" />
        {/if}
      </div>

      <div class="pb-8">
        <slot name="main">Main content goes here</slot>
      </div>
    </div>
  {/if}
</DynamicHeadingContext>
